import type { FC, ReactNode } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Container, Nav, Navbar, NavDropdown } from 'react-bootstrap';
import { useAuth } from '@hooks/useAuth';
import { useEffect, useState } from 'react';
import { employeeService } from '@services/employeeService';
import type { Employee } from '@/types';
import logoLight from '../assets/img/illsol_logo_light.svg?url';
import logoDark from '../assets/img/illsol_logo_dark.svg?url';

interface LayoutProps {
  children: ReactNode;
  showHomeTabs?: boolean;
  activeTab?: string;
  onTabChange?: (tab: string) => void;
}

const Layout: FC<LayoutProps> = ({ children, showHomeTabs = false, activeTab, onTabChange }) => {
  const { user, isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [viewingAsEmail, setViewingAsEmail] = useState<string>('');
  const [isDarkMode, setIsDarkMode] = useState<boolean>(
    window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches
  );

  // Listen for dark mode changes
  useEffect(() => {
    if (!window.matchMedia) return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e: MediaQueryListEvent) => setIsDarkMode(e.matches);

    mediaQuery.addEventListener('change', handler);
    return () => mediaQuery.removeEventListener('change', handler);
  }, []);

  // Fetch employees if user is admin and on portal page
  useEffect(() => {
    if (user?.admin && location.pathname === '/portal') {
      employeeService.getAllEmployees().then(setEmployees);
    }
  }, [user?.admin, location.pathname]);

  // Get viewing email from URL or default to current user
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const emailParam = params.get('email');
    setViewingAsEmail(emailParam || user?.email || '');
  }, [location.search, user?.email]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleViewAsChange = (email: string) => {
    navigate(`/portal?email=${email}`);
  };

  const handleTabClick = (e: React.MouseEvent, tab: string) => {
    e.preventDefault();
    if (onTabChange) {
      onTabChange(tab);
    }
    // Update URL hash without causing page jump
    window.history.pushState(null, '', `#${tab}`);
  };

  // Handle hash changes for direct navigation
  useEffect(() => {
    if (showHomeTabs && location.hash) {
      const tab = location.hash.substring(1); // Remove the #
      if (onTabChange && ['mission', 'services', 'about', 'contact'].includes(tab)) {
        onTabChange(tab);
      }
    }
  }, [location.hash, showHomeTabs, onTabChange]);

  return (
    <>
      <div id="alert_container"></div>
      <Navbar bg={isDarkMode ? 'dark' : 'light'} variant={isDarkMode ? 'dark' : 'light'} expand="md" sticky="top">
        <Container fluid>
          <Navbar.Brand as={Link} to="/">
            <img
              src={isDarkMode ? logoLight : logoDark}
              alt="Illuminative Solutions"
              width="100"
              height="106"
              style={{ width: '100px', height: '106px' }}
            />
          </Navbar.Brand>
          <Navbar.Toggle aria-controls="navbarCollapse" />
          <Navbar.Collapse id="navbarCollapse">
            <Nav className="me-auto" variant="pills" activeKey={activeTab}>
              {showHomeTabs && location.pathname === '/' && (
                <>
                  <Nav.Link
                    eventKey="mission"
                    href="#mission"
                    onClick={(e) => handleTabClick(e, 'mission')}
                    active={activeTab === 'mission'}
                  >
                    Our Mission
                  </Nav.Link>
                  <Nav.Link
                    eventKey="services"
                    href="#services"
                    onClick={(e) => handleTabClick(e, 'services')}
                    active={activeTab === 'services'}
                  >
                    Our Services
                  </Nav.Link>
                  <Nav.Link
                    eventKey="about"
                    href="#about"
                    onClick={(e) => handleTabClick(e, 'about')}
                    active={activeTab === 'about'}
                  >
                    About Us
                  </Nav.Link>
                  <Nav.Link
                    eventKey="contact"
                    href="#contact"
                    onClick={(e) => handleTabClick(e, 'contact')}
                    active={activeTab === 'contact'}
                  >
                    Contact Us
                  </Nav.Link>
                </>
              )}
            </Nav>
            <Nav>
              {isAuthenticated && user?.admin && location.pathname === '/portal' && employees.length > 0 && (
                <NavDropdown
                  title={`Viewing as ${employees.find(e => e.email.S === viewingAsEmail)?.name?.S || viewingAsEmail}`}
                  id="viewing-as-dropdown"
                  menuVariant={isDarkMode ? 'dark' : 'light'}
                >
                  {employees.map(emp => (
                    <NavDropdown.Item
                      key={emp.email.S}
                      onClick={() => handleViewAsChange(emp.email.S)}
                      active={emp.email.S === viewingAsEmail}
                    >
                      {emp.name?.S || emp.email.S}
                    </NavDropdown.Item>
                  ))}
                </NavDropdown>
              )}
              {isAuthenticated && user && location.pathname === '/portal' && (
                <NavDropdown
                  title={user.name || user.email}
                  id="user-dropdown"
                  menuVariant={isDarkMode ? 'dark' : 'light'}
                >
                  <NavDropdown.Item onClick={handleLogout}>Logout</NavDropdown.Item>
                </NavDropdown>
              )}
              <Nav.Link as={Link} to="/portal">
                Employee Portal
              </Nav.Link>
            </Nav>
          </Navbar.Collapse>
        </Container>
      </Navbar>
      <main className="flex-grow-1">
        {children}
      </main>
    </>
  );
};

export default Layout;
